/* ------------------------------------------------------------
 * GT B2B — hero slideshow (sections/image-banner.liquid).
 *
 * Loaded (deferred) only when the hero has more than one slide. No dependencies,
 * no globals: a single custom element whose connected/disconnected callbacks are
 * the whole lifecycle. That is what makes the Theme Editor safe — when Shopify
 * re-renders the section, the old element is disconnected (timer cleared, document
 * listeners removed) and the new one initialises from scratch, so timers and
 * listeners can never accumulate.
 *
 * Deferred images: slides 2-3 render with `hidden` + loading="lazy", so the
 * browser does not fetch them at all. They are revealed after `load` (or on first
 * interaction, whichever comes first) so they never compete with the slide 1 LCP
 * image. They are absolutely positioned, so revealing them shifts nothing.
 * ------------------------------------------------------------ */

(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('gt-hero-slider')) return;

  var SWIPE_MIN = 40;

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  class GtHeroSlider extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.timer = null;
      this.revealed = false;
      this.hovered = false;
      this.focused = false;
      this.offscreen = false;
      this.onClick = this.onClick.bind(this);
      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.reveal = this.reveal.bind(this);
    }

    connectedCallback() {
      this.count = parseInt(this.dataset.gtHeroCount, 10) || 1;
      if (this.count < 2) return;

      this.layers = Array.prototype.slice.call(this.querySelectorAll('[data-gt-hero-index]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-gt-hero-dot]'));
      this.interval = parseInt(this.dataset.gtHeroInterval, 10) || 6000;
      this.autoplay = this.dataset.gtHeroAutoplay !== 'false' && !reducedMotion();

      var dotsWrap = this.querySelector('[data-gt-hero-dots]');
      if (dotsWrap) dotsWrap.removeAttribute('hidden');

      this.addEventListener('click', this.onClick);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      // Perf (2026-10): pause autoplay while the hero is scrolled out of view, so the
      // full-width 600ms crossfade never repaints underneath the rest of the page
      // while visitors scroll. Resumes (full interval) when it comes back into view.
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.offscreen = !entries[entries.length - 1].isIntersecting;
          if (this.offscreen) this.stop();
          else if (this.revealed) this.start();
        });
        this.io.observe(this);
      }

      // Reveal the deferred slide images once the critical load is done, then
      // start rotating. Waiting for `load` is what keeps them out of the LCP race.
      if (document.readyState === 'complete') {
        this.reveal();
        this.start();
      } else {
        this.onLoad = () => {
          this.reveal();
          this.start();
        };
        window.addEventListener('load', this.onLoad, { once: true });
      }
    }

    disconnectedCallback() {
      this.stop();
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.onLoad) window.removeEventListener('load', this.onLoad);
    }

    /* ---- image deferral ---- */

    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      var deferred = this.querySelectorAll('[data-gt-hero-deferred]');
      for (var i = 0; i < deferred.length; i++) deferred[i].removeAttribute('hidden');
    }

    /* ---- slide state ---- */

    select(next, fromUser) {
      if (next === this.index || this.count < 2) return;
      this.index = (next + this.count) % this.count;

      for (var i = 0; i < this.layers.length; i++) {
        var layer = this.layers[i];
        var active = parseInt(layer.dataset.gtHeroIndex, 10) === this.index;
        layer.classList.toggle('is-active', active);
        // Only the copy layers get aria-hidden; the media layers are decorative.
        // (visibility:hidden already keeps inactive CTAs out of the tab order.)
        if (layer.classList.contains('banner__content')) {
          if (active) layer.removeAttribute('aria-hidden');
          else layer.setAttribute('aria-hidden', 'true');
        }
      }

      for (var d = 0; d < this.dots.length; d++) {
        var isCurrent = parseInt(this.dots[d].dataset.gtHeroDot, 10) === this.index;
        this.dots[d].setAttribute('aria-current', isCurrent ? 'true' : 'false');
      }

      // Manual interaction restarts the countdown so the next slide always gets a
      // full interval rather than whatever was left over.
      if (fromUser) this.restart();
    }

    next() {
      this.select(this.index + 1, false);
    }

    /* ---- autoplay ---- */

    start() {
      if (!this.autoplay || this.count < 2 || this.timer) return;
      if (this.hovered || this.focused || this.offscreen || document.hidden) return;
      var self = this;
      this.timer = setInterval(function () {
        self.next();
      }, this.interval);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restart() {
      this.stop();
      this.start();
    }

    /* ---- events ---- */

    onClick(event) {
      var dot = event.target.closest ? event.target.closest('[data-gt-hero-dot]') : null;
      if (!dot || !this.contains(dot)) return;
      this.reveal();
      this.select(parseInt(dot.dataset.gtHeroDot, 10), true);
    }

    onEnter() {
      this.hovered = true;
      this.stop();
    }

    onLeave() {
      this.hovered = false;
      this.start();
    }

    onFocusIn() {
      this.focused = true;
      this.stop();
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.focused = false;
      this.start();
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else this.start();
    }

    onTouchStart(event) {
      if (!event.touches || event.touches.length !== 1) return;
      this.touchX = event.touches[0].clientX;
      this.touchY = event.touches[0].clientY;
    }

    onTouchEnd(event) {
      if (this.touchX == null || !event.changedTouches || !event.changedTouches.length) return;
      var dx = event.changedTouches[0].clientX - this.touchX;
      var dy = event.changedTouches[0].clientY - this.touchY;
      this.touchX = null;
      this.touchY = null;
      // Ignore anything that reads as a vertical scroll.
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.reveal();
      this.select(this.index + (dx < 0 ? 1 : -1), true);
    }

    /* ---- Theme Editor ---- */

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      var layer = event.target.closest('[data-gt-hero-index]');
      this.reveal();
      this.stop();
      if (layer) this.select(parseInt(layer.dataset.gtHeroIndex, 10), false);
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.start();
    }
  }

  customElements.define('gt-hero-slider', GtHeroSlider);
})();
